import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  ArrayParam,
  BooleanParam,
  NumericArrayParam,
  useQueryParam,
  withDefault
} from 'use-query-params';

import { ApiParams, FetchError } from '../../common/api/api';
import {
  ApiResult,
  FetchApiCallback,
  useApiDelete,
  useApiGet,
  useApiPatch,
  useApiPost
} from '../../common/api/Api.hooks';
import { useDownloadBlob } from '../../common/blob.hook';
import {
  IdSearchableFetchOptions,
  useIdSearchableFetch
} from '../../common/entityId.hooks';
import { PaginatedList } from '../../common/PaginatedList.interface';
import { FileDto } from '../documents/Document.interface';
import { Hazard } from '../hazards/Hazard.interface';
import { WorkflowEntity } from '../workflow/Workflow.interface';
import { ProductBoardColumnEntity } from './board/ProductBoardColumn';
import { ProductFilterOptions } from './components/ProductBoardFilter';
import {
  getProductEntityIdPrefix,
  getProductEntityName
} from './Product.config';
import {
  Product,
  ProductBoardData,
  ProductComponent,
  ProductDesignInput,
  ProductDesignInputVersion,
  ProductDesignOutput,
  ProductDesignOutputVersion,
  ProductDesignSpec,
  ProductDesignSpecVersion,
  ProductEntity,
  ProductExportEntity,
  ProductRelationMap,
  ProductRelease,
  ProductTemplate,
  ProductUserNeed,
  ProductUserNeedVersion,
  ProductValidation,
  ProductValidationVersion,
  ProductVerification,
  ProductVerificationVersion,
  ProductWorkflowEntities
} from './Product.interface';

export function useCreateProduct() {
  const { data, isLoading, error, post } = useApiPost<Product>('products');
  return {
    createdProduct: data,
    isCreating: isLoading,
    createError: error,
    createProduct: post
  };
}

export function useUpdateProduct(id: number) {
  const { data, isLoading, error, patch } = useApiPatch<Product>(
    `products/${id}`
  );
  return {
    updatedProduct: data,
    isUpdating: isLoading,
    updateError: error,
    updateProduct: patch
  };
}

export function useDeleteProduct(id: number) {
  const { data, del, ...result } = useApiDelete<Product>(`products/${id}`);
  return { ...result, deletedProduct: data, deleteProduct: del };
}

export function useFindProducts() {
  const { data, isLoading, error, get } =
    useApiGet<PaginatedList<Product>>('products');
  const findProducts = useIdSearchableFetch(get, WorkflowEntity.Product);
  return {
    productList: data,
    isLoading,
    loadingError: error,
    findProducts
  };
}

export function useFindProduct(id: number, shouldFetchOnMount = false) {
  const { data, isLoading, error, get } = useApiGet<Product>(`products/${id}`);
  useEffect(() => {
    if (shouldFetchOnMount) {
      get();
    }
  }, [get, shouldFetchOnMount]);
  return {
    product: data,
    isLoading,
    loadingError: error,
    findProduct: get
  };
}

export function useCreateProductUserNeed(productId: number) {
  const { data, isLoading, error, post } = useApiPost<ProductUserNeed>(
    `products/${productId}/user-needs`
  );
  return {
    createdProductUserNeed: data,
    isCreatingProductUserNeed: isLoading,
    createProductUserNeedError: error,
    createProductUserNeed: post
  };
}
export function useUpdateProductUserNeed(
  productId: number,
  productUserNeedId?: number
) {
  const [path, setPath] = useState('');
  const [userNeed, setUserNeed] = useState<Partial<ProductUserNeed>>();
  const { data, isLoading, error, patch } = useApiPatch<ProductUserNeed>(path);
  const updateProductUserNeed = useCallback(
    (userNeed: Partial<ProductUserNeed>) => {
      setPath(
        `products/${productId}/user-needs/${productUserNeedId || userNeed.id}`
      );
      setUserNeed(userNeed);
    },
    [productId, productUserNeedId]
  );
  useEffect(() => {
    if (path && userNeed) {
      patch(userNeed);
    }
  }, [patch, path, userNeed]);
  return {
    updatedProductUserNeed: data,
    isUpdatingProductUserNeed: isLoading,
    updateProductUserNeedError: error,
    updateProductUserNeed
  };
}
export function useFindProductUserNeeds(productId: number) {
  const { data, isLoading, error, get } = useApiGet<
    PaginatedList<ProductUserNeed>
  >(`products/${productId}/user-needs`);
  const findProductUserNeeds = useIdSearchableFetch(
    get,
    WorkflowEntity.UserNeed
  );
  return {
    productUserNeedList: data,
    isLoadingProductUserNeeds: isLoading,
    loadingProductUserNeedsError: error,
    findProductUserNeeds
  };
}

export function useFindProductUserNeedsById(
  productId: number,
  productUserNeedId: number
) {
  const path = `products/${productId}/user-needs/${productUserNeedId}`;
  const { data, isLoading, error, get } = useApiGet<ProductUserNeed>(path);

  return {
    productUserNeed: data,
    isLoadingProductUserNeeds: isLoading,
    findProductUserNeedError: error,
    findProductUserNeed: get
  };
}

export function useFindProductUserNeedsByDesignInputId(
  productId: number,
  designInputId: number
) {
  const { data, isLoading, error, get } = useApiGet<ProductUserNeed[]>(
    `products/${productId}/design-inputs/${designInputId}/user-needs`
  );
  const findProductUserNeeds = useIdSearchableFetch(
    get,
    WorkflowEntity.UserNeed
  );
  return {
    productUserNeedList: data,
    isLoadingProductUserNeeds: isLoading,
    loadingProductUserNeedsError: error,
    findProductUserNeeds
  };
}

export function useFindProductUserNeedsByDesignOutputId(
  productId: number,
  designOutputId: number
) {
  const { data, isLoading, error, get } = useApiGet<ProductUserNeed[]>(
    `products/${productId}/design-outputs/${designOutputId}/user-needs`
  );
  const findProductUserNeeds = useIdSearchableFetch(
    get,
    WorkflowEntity.UserNeed
  );
  return {
    productUserNeedList: data,
    isLoadingProductUserNeeds: isLoading,
    loadingProductUserNeedsError: error,
    findProductUserNeeds
  };
}

export function useFindProductUserNeedsByVerificationId(
  productId: number,
  verificationId: number
) {
  const { data, isLoading, error, get } = useApiGet<ProductUserNeed[]>(
    `products/${productId}/verifications/${verificationId}/user-needs`
  );
  const findProductUserNeeds = useIdSearchableFetch(
    get,
    WorkflowEntity.UserNeed
  );
  return {
    productUserNeedList: data,
    isLoadingProductUserNeeds: isLoading,
    loadingProductUserNeedsError: error,
    findProductUserNeeds
  };
}

export function useFindProductUserNeedsByValidationId(
  productId: number,
  validationId: number
) {
  const { data, isLoading, error, get } = useApiGet<ProductUserNeed[]>(
    `products/${productId}/validations/${validationId}/user-needs`
  );
  const findProductUserNeeds = useIdSearchableFetch(
    get,
    WorkflowEntity.UserNeed
  );
  return {
    productUserNeedList: data,
    isLoadingProductUserNeeds: isLoading,
    loadingProductUserNeedsError: error,
    findProductUserNeeds
  };
}

export function useProductEntityState<
  Entity extends Partial<ProductEntity> = Partial<ProductEntity>
>(
  entitiesProp: Entity[]
): [Entity[], (entities: Entity[], concat?: boolean) => void] {
  const entitiesRef = useRef<Entity[]>(entitiesProp);
  const [entities, setEntities] = useState<Entity[]>(entitiesProp);

  const addEntities = useCallback(
    (newEntities: Entity[], concat: boolean = false) => {
      if (!concat) {
        entitiesRef.current = newEntities;
        return setEntities(newEntities);
      }

      // remove existing entities
      const values: Entity[] = newEntities
        .filter((newEntity: Entity) => {
          return !entitiesRef.current?.find(
            (entity: Entity) => entity.id === newEntity.id
          );
        })
        .concat(entitiesRef.current || [])
        .filter((entity: Entity) => typeof entity.id !== 'undefined');

      values.sort((a: Entity, b: Entity) => {
        if (typeof a.id === 'undefined') {
          return -1;
        } else if (typeof b.id === 'undefined') {
          return 1;
        }
        return b.id! - a.id!;
      });
      entitiesRef.current = values;
      setEntities(values);
    },
    []
  );

  return [entities, addEntities];
}

export function useFindProductUserNeedsByDesignSpecId(
  productId: number,
  designSpecId: number
) {
  const { data, isLoading, error, get } = useApiGet<ProductUserNeed[]>(
    `products/${productId}/design-specs/${designSpecId}/user-needs`
  );
  const findProductUserNeeds = useIdSearchableFetch(
    get,
    WorkflowEntity.UserNeed
  );
  return {
    productUserNeedList: data,
    isLoadingProductUserNeeds: isLoading,
    loadingProductUserNeedsError: error,
    findProductUserNeeds
  };
}

export function useFindFilteredProductUserNeeds(
  productId: number,
  productEntityId: number = 0,
  entity?: ProductBoardColumnEntity
): {
  productUserNeedList: ProductUserNeed[] | undefined;
  productUserNeedPaginated?: PaginatedList<ProductUserNeed>;
  isLoadingProductUserNeeds: boolean;
  loadingProductUserNeedsError: FetchError | undefined;
  findProductUserNeeds: FetchApiCallback;
} {
  const find = useFindProductUserNeeds(productId);
  const findByDesignInputId = useFindProductUserNeedsByDesignInputId(
    productId,
    productEntityId
  );
  const findByDesignOutputId = useFindProductUserNeedsByDesignOutputId(
    productId,
    productEntityId
  );
  const findByVerificationId = useFindProductUserNeedsByVerificationId(
    productId,
    productEntityId
  );
  const findByValidationId = useFindProductUserNeedsByValidationId(
    productId,
    productEntityId
  );

  switch (entity) {
    case ProductBoardColumnEntity.DesignInput:
      return findByDesignInputId;
    case ProductBoardColumnEntity.DesignOutput:
      return findByDesignOutputId;
    case ProductBoardColumnEntity.Verification:
      return findByVerificationId;
    case ProductBoardColumnEntity.Validation:
      return findByValidationId;
    default: {
      const { productUserNeedList, ...results } = find;
      return {
        ...results,
        productUserNeedPaginated: productUserNeedList,
        productUserNeedList: productUserNeedList?.data
      };
    }
  }
}

export function useAssignUserNeedDesignInput(
  productId: number,
  userNeedId: number
) {
  const { data, isLoading, error, post } = useApiPost<ProductUserNeed>(
    `products/${productId}/user-needs/${userNeedId}/design-inputs`
  );
  return {
    updatedProductUserNeed: data,
    isAssigning: isLoading,
    error,
    assignDesignInputs: post
  };
}

export function useAssignUserNeedDesignSpec(
  productId: number,
  userNeedId: number
) {
  const { data, isLoading, error, post } = useApiPost<ProductUserNeed>(
    `products/${productId}/user-needs/${userNeedId}/design-specs`
  );
  return {
    updatedProductUserNeed: data,
    isAssigning: isLoading,
    error,
    assignDesignSpecs: post
  };
}

export function useAssignUserNeedVerification(
  productId: number,
  userNeedId: number
) {
  const { data, isLoading, error, post } = useApiPost<ProductUserNeed>(
    `products/${productId}/user-needs/${userNeedId}/verifications`
  );
  return {
    updatedProductUserNeed: data,
    isAssigning: isLoading,
    error,
    assignVerifications: post
  };
}

export function useAssignUserNeedValidation(
  productId: number,
  userNeedId: number
) {
  const { data, isLoading, error, post } = useApiPost<ProductUserNeed>(
    `products/${productId}/user-needs/${userNeedId}/validations`
  );
  return {
    updatedProductUserNeed: data,
    isAssigning: isLoading,
    error,
    assignValidations: post
  };
}

export function useAssignUserNeedHazard(productId: number, userNeedId: number) {
  const { data, isLoading, error, post } = useApiPost<ProductUserNeed>(
    `products/${productId}/user-needs/${userNeedId}/hazards`
  );
  return {
    updatedProductUserNeed: data,
    isAssigning: isLoading,
    error,
    assignHazards: post
  };
}

export function useCreateProductDesignInput(productId: number) {
  const { data, isLoading, error, post } = useApiPost<ProductDesignInput>(
    `products/${productId}/design-inputs`
  );
  return {
    createdProductDesignInput: data,
    isCreatingProductDesignInput: isLoading,
    createProductDesignInputError: error,
    createProductDesignInput: post
  };
}
export function useUpdateProductDesignInput(
  productId: number,
  productDesignInputId?: number
) {
  const [path, setPath] = useState('');
  const [designInput, setDesignInput] = useState<Partial<ProductDesignInput>>();
  const { data, isLoading, error, patch } =
    useApiPatch<ProductDesignInput>(path);
  const updateProductDesignInput = useCallback(
    (designInput: Partial<ProductDesignInput>) => {
      setPath(
        `products/${productId}/design-inputs/${
          productDesignInputId || designInput.id
        }`
      );
      setDesignInput(designInput);
    },
    [productDesignInputId, productId]
  );
  useEffect(() => {
    if (path && designInput) {
      patch(designInput);
    }
  }, [patch, path, designInput]);
  return {
    updatedProductDesignInput: data,
    isUpdatingProductDesignInput: isLoading,
    updateProductDesignInputError: error,
    updateProductDesignInput
  };
}
export function useFindProductDesignInputs(productId: number) {
  const { data, isLoading, error, get } = useApiGet<
    PaginatedList<ProductDesignInput>
  >(`products/${productId}/design-inputs`);
  const findProductDesignInputs = useIdSearchableFetch(
    get,
    WorkflowEntity.DesignInput
  );
  return {
    productDesignInputList: data,
    isLoadingProductDesignInputs: isLoading,
    loadingProductDesignInputsError: error,
    findProductDesignInputs
  };
}

export function useFindDesignInputsByUserNeedId(
  productId: number,
  userNeedId: number
) {
  const { data, isLoading, error, get } = useApiGet<ProductDesignInput[]>(
    `products/${productId}/user-needs/${userNeedId}/design-inputs`
  );
  const findProductDesignInputs = useIdSearchableFetch(
    get,
    WorkflowEntity.DesignInput
  );
  return {
    productDesignInputs: data,
    isLoadingProductDesignInputs: isLoading,
    loadingProductDesignInputsError: error,
    findProductDesignInputs
  };
}

export function useFindProductDesignInputById(
  productId: number,
  designInputId: number
) {
  const { data, isLoading, error, get } = useApiGet<ProductDesignInput>(
    `products/${productId}/design-inputs/${designInputId}`
  );
  return {
    productDesignInput: data,
    isLoadingProductDesignInput: isLoading,
    findProductDesignInputError: error,
    findProductDesignInput: get
  };
}

export function useFindProductDesignInputsByUserNeedId(
  productId: number,
  userNeedId: number
) {
  const { data, isLoading, error, get } = useApiGet<ProductDesignInput[]>(
    `products/${productId}/user-needs/${userNeedId}/design-inputs`
  );
  const findProductDesignInputs = useIdSearchableFetch(
    get,
    WorkflowEntity.DesignInput
  );
  return {
    productDesignInputList: data,
    isLoadingProductDesignInputs: isLoading,
    loadingProductDesignInputsError: error,
    findProductDesignInputs
  };
}

export function useFindProductDesignInputsByDesignOutputId(
  productId: number,
  designOutputId: number
) {
  const { data, isLoading, error, get } = useApiGet<ProductDesignInput[]>(
    `products/${productId}/design-outputs/${designOutputId}/design-inputs`
  );
  const findProductDesignInputs = useIdSearchableFetch(
    get,
    WorkflowEntity.DesignInput
  );
  return {
    productDesignInputList: data,
    isLoadingProductDesignInputs: isLoading,
    loadingProductDesignInputsError: error,
    findProductDesignInputs
  };
}

export function useFindProductDesignInputsByVerificationId(
  productId: number,
  verificationId: number
) {
  const { data, isLoading, error, get } = useApiGet<ProductDesignInput[]>(
    `products/${productId}/verifications/${verificationId}/design-inputs`
  );
  const findProductDesignInputs = useIdSearchableFetch(
    get,
    WorkflowEntity.DesignInput
  );
  return {
    productDesignInputList: data,
    isLoadingProductDesignInputs: isLoading,
    loadingProductDesignInputsError: error,
    findProductDesignInputs
  };
}

export function useFindProductDesignInputsByValidationId(
  productId: number,
  validationId: number
) {
  const { data, isLoading, error, get } = useApiGet<ProductDesignInput[]>(
    `products/${productId}/validations/${validationId}/design-inputs`
  );
  const findProductDesignInputs = useIdSearchableFetch(
    get,
    WorkflowEntity.DesignInput
  );
  return {
    productDesignInputList: data,
    isLoadingProductDesignInputs: isLoading,
    loadingProductDesignInputsError: error,
    findProductDesignInputs
  };
}

export function useFindFilteredProductDesignInputs(
  productId: number,
  productEntityId: number = 0,
  entity?: ProductBoardColumnEntity
): {
  productDesignInputList: ProductDesignInput[] | undefined;
  productDesignInputPaginated?: PaginatedList<ProductDesignInput>;
  isLoadingProductDesignInputs: boolean;
  loadingProductDesignInputsError: FetchError | undefined;
  findProductDesignInputs: FetchApiCallback;
} {
  const find = useFindProductDesignInputs(productId);
  const findByUserNeedId = useFindProductDesignInputsByUserNeedId(
    productId,
    productEntityId
  );
  const findByDesignOutputId = useFindProductDesignInputsByDesignOutputId(
    productId,
    productEntityId
  );
  const findByVerificationId = useFindProductDesignInputsByVerificationId(
    productId,
    productEntityId
  );
  const findByValidationId = useFindProductDesignInputsByValidationId(
    productId,
    productEntityId
  );

  switch (entity) {
    case ProductBoardColumnEntity.UserNeed:
      return findByUserNeedId;
    case ProductBoardColumnEntity.DesignOutput:
      return findByDesignOutputId;
    case ProductBoardColumnEntity.Verification:
      return findByVerificationId;
    case ProductBoardColumnEntity.Validation:
      return findByValidationId;
    default: {
      const { productDesignInputList, ...results } = find;
      return {
        ...results,
        productDesignInputPaginated: productDesignInputList,
        productDesignInputList: productDesignInputList?.data
      };
    }
  }
}

export function useAssignDesignInputUserNeed(
  productId: number,
  designInputId: number
) {
  const { data, isLoading, error, post } = useApiPost<ProductDesignInput>(
    `products/${productId}/design-inputs/${designInputId}/user-needs`
  );
  return {
    updatedProductDesignInput: data,
    isAssigning: isLoading,
    error,
    assignUserNeeds: post
  };
}

export function useAssignDesignInputDesignOutput(
  productId: number,
  designInputId: number
) {
  const { data, isLoading, error, post } = useApiPost<ProductDesignInput>(
    `products/${productId}/design-inputs/${designInputId}/design-outputs`
  );
  return {
    updatedProductDesignInput: data,
    isAssigning: isLoading,
    error,
    assignDesignOutputs: post
  };
}

export function useAssignDesignInputDesignSpec(
  productId: number,
  designInputId: number
) {
  const { data, isLoading, error, post } = useApiPost<ProductDesignInput>(
    `products/${productId}/design-inputs/${designInputId}/design-specs`
  );
  return {
    updatedProductDesignInput: data,
    isAssigning: isLoading,
    error,
    assignDesignSpecs: post
  };
}

export function useAssignDesignInputVerification(
  productId: number,
  designInputId: number
) {
  const { data, isLoading, error, post } = useApiPost<ProductDesignInput>(
    `products/${productId}/design-inputs/${designInputId}/verifications`
  );
  return {
    updatedProductDesignInput: data,
    isAssigning: isLoading,
    error,
    assignVerifications: post
  };
}

export function useAssignDesignInputValidation(
  productId: number,
  designInputId: number
) {
  const { data, isLoading, error, post } = useApiPost<ProductDesignInput>(
    `products/${productId}/design-inputs/${designInputId}/validations`
  );
  return {
    updatedProductDesignInput: data,
    isAssigning: isLoading,
    error,
    assignValidations: post
  };
}

export function useCreateProductDesignOutput(productId: number) {
  const { data, isLoading, error, post } = useApiPost<ProductDesignOutput>(
    `products/${productId}/design-outputs`
  );
  return {
    createdProductDesignOutput: data,
    isCreatingProductDesignOutput: isLoading,
    createProductDesignOutputError: error,
    createProductDesignOutput: post
  };
}
export function useUpdateProductDesignOutput(
  productId: number,
  productDesignOutputId?: number
) {
  const [path, setPath] = useState('');
  const [designOutput, setDesignOutput] =
    useState<Partial<ProductDesignOutput>>();
  const { data, isLoading, error, patch } =
    useApiPatch<ProductDesignOutput>(path);
  const updateProductDesignOutput = useCallback(
    (designOutput: Partial<ProductDesignOutput>) => {
      setPath(
        `products/${productId}/design-outputs/${
          productDesignOutputId || designOutput.id
        }`
      );
      setDesignOutput(designOutput);
    },
    [productDesignOutputId, productId]
  );
  useEffect(() => {
    if (path && designOutput) {
      patch(designOutput);
    }
  }, [patch, path, designOutput]);

  return {
    updatedProductDesignOutput: data,
    isUpdatingProductDesignOutput: isLoading,
    updateProductDesignOutputError: error,
    updateProductDesignOutput
  };
}
export function useFindProductDesignOutputs(productId: number) {
  const { data, isLoading, error, get } = useApiGet<
    PaginatedList<ProductDesignOutput>
  >(`products/${productId}/design-outputs`);
  const findProductDesignOutputs = useIdSearchableFetch(
    get,
    WorkflowEntity.DesignOutput
  );
  return {
    productDesignOutputList: data,
    isLoadingProductDesignOutputs: isLoading,
    loadingProductDesignOutputsError: error,
    findProductDesignOutputs
  };
}

export function useFindProductDesignOutputById(
  productId: number,
  designOutputId: number
) {
  const { data, isLoading, error, get } = useApiGet<ProductDesignOutput>(
    `products/${productId}/design-outputs/${designOutputId}`
  );
  return {
    productDesignOutput: data,
    isLoadingProductDesignOutput: isLoading,
    findProductDesignOutputError: error,
    findProductDesignOutput: get
  };
}

export function useFindProductDesignOutputsByUserNeedId(
  productId: number,
  userNeedId: number
) {
  const { data, isLoading, error, get } = useApiGet<ProductDesignOutput[]>(
    `products/${productId}/user-needs/${userNeedId}/design-outputs`
  );
  const findProductDesignOutputs = useIdSearchableFetch(
    get,
    WorkflowEntity.DesignOutput
  );
  return {
    productDesignOutputList: data,
    isLoadingProductDesignOutputs: isLoading,
    loadingProductDesignOutputsError: error,
    findProductDesignOutputs
  };
}

export function useFindProductDesignOutputsByDesignInputId(
  productId: number,
  designInputId: number
) {
  const { data, isLoading, error, get } = useApiGet<ProductDesignOutput[]>(
    `products/${productId}/design-inputs/${designInputId}/design-outputs`
  );
  const findProductDesignOutputs = useIdSearchableFetch(
    get,
    WorkflowEntity.DesignOutput
  );
  return {
    productDesignOutputList: data,
    isLoadingProductDesignOutputs: isLoading,
    loadingProductDesignOutputsError: error,
    findProductDesignOutputs
  };
}

export function useFindProductDesignOutputsByVerificationId(
  productId: number,
  verificationId: number
) {
  const { data, isLoading, error, get } = useApiGet<ProductDesignOutput[]>(
    `products/${productId}/verifications/${verificationId}/design-outputs`
  );
  const findProductDesignOutputs = useIdSearchableFetch(
    get,
    WorkflowEntity.DesignOutput
  );
  return {
    productDesignOutputList: data,
    isLoadingProductDesignOutputs: isLoading,
    loadingProductDesignOutputsError: error,
    findProductDesignOutputs
  };
}

export function useFindProductDesignOutputsByValidationId(
  productId: number,
  validationId: number
) {
  const { data, isLoading, error, get } = useApiGet<ProductDesignOutput[]>(
    `products/${productId}/validations/${validationId}/design-outputs`
  );
  const findProductDesignOutputs = useIdSearchableFetch(
    get,
    WorkflowEntity.DesignOutput
  );
  return {
    productDesignOutputList: data,
    isLoadingProductDesignOutputs: isLoading,
    loadingProductDesignOutputsError: error,
    findProductDesignOutputs
  };
}

export function useFindFilteredProductDesignOutputs(
  productId: number,
  productEntityId: number = 0,
  entity?: ProductBoardColumnEntity
): {
  productDesignOutputList: ProductDesignOutput[] | undefined;
  productDesignOutputPaginated?: PaginatedList<ProductDesignOutput>;
  isLoadingProductDesignOutputs: boolean;
  loadingProductDesignOutputsError: FetchError | undefined;
  findProductDesignOutputs: FetchApiCallback;
} {
  const find = useFindProductDesignOutputs(productId);
  const findByUserNeedId = useFindProductDesignOutputsByUserNeedId(
    productId,
    productEntityId
  );
  const findByDesignInputId = useFindProductDesignOutputsByDesignInputId(
    productId,
    productEntityId
  );
  const findByVerificationId = useFindProductDesignOutputsByVerificationId(
    productId,
    productEntityId
  );
  const findByValidationId = useFindProductDesignOutputsByValidationId(
    productId,
    productEntityId
  );

  switch (entity) {
    case ProductBoardColumnEntity.UserNeed:
      return findByUserNeedId;
    case ProductBoardColumnEntity.DesignInput:
      return findByDesignInputId;
    case ProductBoardColumnEntity.Verification:
      return findByVerificationId;
    case ProductBoardColumnEntity.Validation:
      return findByValidationId;
    default: {
      const { productDesignOutputList, ...results } = find;
      return {
        ...results,
        productDesignOutputPaginated: productDesignOutputList,
        productDesignOutputList: productDesignOutputList?.data
      };
    }
  }
}

export function useAssignDesignOutputDesignInput(
  productId: number,
  designOutputId: number
) {
  const { data, isLoading, error, post } = useApiPost<ProductDesignOutput>(
    `products/${productId}/design-outputs/${designOutputId}/design-inputs`
  );
  return {
    updatedProductDesignOutput: data,
    isAssigning: isLoading,
    error,
    assignDesignInputs: post
  };
}

export function useAssignDesignOutputVerification(
  productId: number,
  designOutputId: number
) {
  const { data, isLoading, error, post } = useApiPost<ProductDesignOutput>(
    `products/${productId}/design-outputs/${designOutputId}/verifications`
  );
  return {
    updatedProductDesignOutput: data,
    isAssigning: isLoading,
    error,
    assignVerifications: post
  };
}

export function useAssignDesignOutputValidation(
  productId: number,
  designOutputId: number
) {
  const { data, isLoading, error, post } = useApiPost<ProductDesignOutput>(
    `products/${productId}/design-outputs/${designOutputId}/validations`
  );
  return {
    updatedProductDesignOutput: data,
    isAssigning: isLoading,
    error,
    assignValidations: post
  };
}

export function useAssignDesignOutputDesignSpec(
  productId: number,
  designOutputId: number
) {
  const { data, isLoading, error, post } = useApiPost<ProductDesignOutput>(
    `products/${productId}/design-outputs/${designOutputId}/design-specs`
  );
  return {
    updatedProductDesignOutput: data,
    isAssigning: isLoading,
    error,
    assignDesignSpecs: post
  };
}

export function useCreateProductVerification(productId: number) {
  const { data, isLoading, error, post } = useApiPost<ProductVerification>(
    `products/${productId}/verifications`
  );
  return {
    createdProductVerification: data,
    isCreatingProductVerification: isLoading,
    createProductVerificationError: error,
    createProductVerification: post
  };
}
export function useUpdateProductVerification(
  productId: number,
  productVerificationId?: number
) {
  const [path, setPath] = useState('');
  const [verification, setVerification] =
    useState<Partial<ProductVerification>>();
  const { data, isLoading, error, patch } =
    useApiPatch<ProductVerification>(path);
  const updateProductVerification = useCallback(
    (verification: Partial<ProductVerification>) => {
      setPath(
        `products/${productId}/verifications/${
          productVerificationId || verification.id
        }`
      );
      setVerification(verification);
    },
    [productId, productVerificationId]
  );
  useEffect(() => {
    if (path && verification) {
      patch(verification);
    }
  }, [patch, path, verification]);

  return {
    updatedProductVerification: data,
    isUpdatingProductVerification: isLoading,
    updateProductVerificationError: error,
    updateProductVerification
  };
}
export function useFindProductVerifications(productId: number) {
  const { data, isLoading, error, get } = useApiGet<
    PaginatedList<ProductVerification>
  >(`products/${productId}/verifications`);
  const findProductVerifications = useIdSearchableFetch(
    get,
    WorkflowEntity.DesignVerification
  );
  return {
    productVerificationList: data,
    isLoadingProductVerifications: isLoading,
    loadingProductVerificationsError: error,
    findProductVerifications
  };
}

export function useFindProductVerificationsByUserNeedId(
  productId: number,
  userNeedId: number
) {
  const { data, isLoading, error, get } = useApiGet<ProductVerification[]>(
    `products/${productId}/user-needs/${userNeedId}/verifications`
  );
  const findProductVerifications = useIdSearchableFetch(
    get,
    WorkflowEntity.DesignVerification
  );
  return {
    productVerificationList: data,
    isLoadingProductVerifications: isLoading,
    loadingProductVerificationsError: error,
    findProductVerifications
  };
}

export function useFindProductVerificationsByDesignInputId(
  productId: number,
  designInputId: number
) {
  const { data, isLoading, error, get } = useApiGet<ProductVerification[]>(
    `products/${productId}/design-inputs/${designInputId}/verifications`
  );
  const findProductVerifications = useIdSearchableFetch(
    get,
    WorkflowEntity.DesignVerification
  );
  return {
    productVerificationList: data,
    isLoadingProductVerifications: isLoading,
    loadingProductVerificationsError: error,
    findProductVerifications
  };
}

export function useFindProductVerificationsByDesignOutputId(
  productId: number,
  designOutputId: number
) {
  const { data, isLoading, error, get } = useApiGet<ProductVerification[]>(
    `products/${productId}/design-outputs/${designOutputId}/verifications`
  );
  const findProductVerifications = useIdSearchableFetch(
    get,
    WorkflowEntity.DesignVerification
  );
  return {
    productVerificationList: data,
    isLoadingProductVerifications: isLoading,
    loadingProductVerificationsError: error,
    findProductVerifications
  };
}

export function useFindProductVerificationsByValidationId(
  productId: number,
  validationId: number
) {
  const { data, isLoading, error, get } = useApiGet<ProductVerification[]>(
    `products/${productId}/validations/${validationId}/verifications`
  );
  const findProductVerifications = useIdSearchableFetch(
    get,
    WorkflowEntity.DesignVerification
  );
  return {
    productVerificationList: data,
    isLoadingProductVerifications: isLoading,
    loadingProductVerificationsError: error,
    findProductVerifications
  };
}

export function useFindFilteredProductVerifications(
  productId: number,
  productEntityId: number = 0,
  entity?: ProductBoardColumnEntity
): {
  productVerificationList: ProductVerification[] | undefined;
  productVerificationPaginated?: PaginatedList<ProductVerification>;
  isLoadingProductVerifications: boolean;
  loadingProductVerificationsError: FetchError | undefined;
  findProductVerifications: FetchApiCallback;
} {
  const find = useFindProductVerifications(productId);
  const findByUserNeedId = useFindProductVerificationsByUserNeedId(
    productId,
    productEntityId
  );
  const findByDesignInputId = useFindProductVerificationsByDesignInputId(
    productId,
    productEntityId
  );
  const findByDesignOutputId = useFindProductVerificationsByDesignOutputId(
    productId,
    productEntityId
  );
  const findByValidationId = useFindProductVerificationsByValidationId(
    productId,
    productEntityId
  );

  switch (entity) {
    case ProductBoardColumnEntity.UserNeed:
      return findByUserNeedId;
    case ProductBoardColumnEntity.DesignInput:
      return findByDesignInputId;
    case ProductBoardColumnEntity.DesignOutput:
      return findByDesignOutputId;
    case ProductBoardColumnEntity.Validation:
      return findByValidationId;
    default: {
      const { productVerificationList, ...results } = find;
      return {
        ...results,
        productVerificationPaginated: productVerificationList,
        productVerificationList: productVerificationList?.data
      };
    }
  }
}

export function useFindProductVerificationById(
  productId: number,
  verificationId: number
) {
  const { data, isLoading, error, get } = useApiGet<ProductVerification>(
    `products/${productId}/verifications/${verificationId}`
  );
  return {
    productVerification: data,
    isLoadingProductVerification: isLoading,
    findProductVerificationError: error,
    findProductVerification: get
  };
}

export function useAssignVerificationDesignOutput(
  productId: number,
  verificationId: number
) {
  const { data, isLoading, error, post } = useApiPost<ProductVerification>(
    `products/${productId}/verifications/${verificationId}/design-outputs`
  );
  return {
    updatedProductVerification: data,
    isAssigning: isLoading,
    error,
    assignDesignOutputs: post
  };
}

export function useCreateProductValidation(productId: number) {
  const { data, isLoading, error, post } = useApiPost<ProductValidation>(
    `products/${productId}/validations`
  );
  return {
    createdProductValidation: data,
    isCreatingProductValidation: isLoading,
    createProductValidationError: error,
    createProductValidation: post
  };
}
export function useUpdateProductValidation(
  productId: number,
  productValidationId?: number
) {
  const [path, setPath] = useState('');
  const [validation, setValidation] = useState<Partial<ProductValidation>>();
  const { data, isLoading, error, patch } =
    useApiPatch<ProductValidation>(path);
  const updateProductValidation = useCallback(
    (validation: Partial<ProductValidation>) => {
      setPath(
        `products/${productId}/validations/${
          productValidationId || validation.id
        }`
      );
      setValidation(validation);
    },
    [productId, productValidationId]
  );
  useEffect(() => {
    if (path && validation) {
      patch(validation);
    }
  }, [patch, path, validation]);

  return {
    updatedProductValidation: data,
    isUpdatingProductValidation: isLoading,
    updateProductValidationError: error,
    updateProductValidation
  };
}
export function useFindProductValidations(productId: number) {
  const { data, isLoading, error, get } = useApiGet<
    PaginatedList<ProductValidation>
  >(`products/${productId}/validations`);
  const findProductValidations = useIdSearchableFetch(
    get,
    WorkflowEntity.DesignValidation
  );
  return {
    productValidationList: data,
    isLoadingProductValidations: isLoading,
    loadingProductValidationsError: error,
    findProductValidations
  };
}

export function useFindProductValidationsByUserNeedId(
  productId: number,
  userNeedId: number
) {
  const { data, isLoading, error, get } = useApiGet<ProductValidation[]>(
    `products/${productId}/user-needs/${userNeedId}/validations`
  );
  const findProductValidations = useIdSearchableFetch(
    get,
    WorkflowEntity.DesignValidation
  );
  return {
    productValidationList: data,
    isLoadingProductValidations: isLoading,
    loadingProductValidationsError: error,
    findProductValidations
  };
}

export function useFindHazardsByUserNeedId(
  productId: number,
  userNeedId: number
) {
  const { data, isLoading, error, get } = useApiGet<Hazard[]>(
    `products/${productId}/user-needs/${userNeedId}/hazards`
  );
  const findHazards = useIdSearchableFetch(get, WorkflowEntity.Hazard);
  return {
    hazardList: data,
    isLoadingHazards: isLoading,
    loadingHazardsError: error,
    findHazards
  };
}

export function useFindProductValidationsByDesignInputId(
  productId: number,
  designInputId: number
) {
  const { data, isLoading, error, get } = useApiGet<ProductValidation[]>(
    `products/${productId}/design-inputs/${designInputId}/validations`
  );
  const findProductValidations = useIdSearchableFetch(
    get,
    WorkflowEntity.DesignValidation
  );
  return {
    productValidationList: data,
    isLoadingProductValidations: isLoading,
    loadingProductValidationsError: error,
    findProductValidations
  };
}

export function useFindProductValidationsByDesignOutputId(
  productId: number,
  designOutputId: number
) {
  const { data, isLoading, error, get } = useApiGet<ProductValidation[]>(
    `products/${productId}/design-outputs/${designOutputId}/validations`
  );
  const findProductValidations = useIdSearchableFetch(
    get,
    WorkflowEntity.DesignValidation
  );
  return {
    productValidationList: data,
    isLoadingProductValidations: isLoading,
    loadingProductValidationsError: error,
    findProductValidations
  };
}

export function useFindProductValidationsByVerificationId(
  productId: number,
  verificationId: number
) {
  const { data, isLoading, error, get } = useApiGet<ProductValidation[]>(
    `products/${productId}/verifications/${verificationId}/validations`
  );
  const findProductValidations = useIdSearchableFetch(
    get,
    WorkflowEntity.DesignValidation
  );
  return {
    productValidationList: data,
    isLoadingProductValidations: isLoading,
    loadingProductValidationsError: error,
    findProductValidations
  };
}

export function useFindFilteredProductValidations(
  productId: number,
  productEntityId: number = 0,
  entity?: ProductBoardColumnEntity
): {
  productValidationList: ProductValidation[] | undefined;
  productValidationPaginated?: PaginatedList<ProductValidation>;
  isLoadingProductValidations: boolean;
  loadingProductValidationsError: FetchError | undefined;
  findProductValidations: FetchApiCallback;
} {
  const find = useFindProductValidations(productId);
  const findByUserNeedId = useFindProductValidationsByUserNeedId(
    productId,
    productEntityId
  );
  const findByDesignInputId = useFindProductValidationsByDesignInputId(
    productId,
    productEntityId
  );
  const findByDesignOutputId = useFindProductValidationsByDesignOutputId(
    productId,
    productEntityId
  );
  const findByVerificationId = useFindProductValidationsByVerificationId(
    productId,
    productEntityId
  );

  switch (entity) {
    case ProductBoardColumnEntity.UserNeed:
      return findByUserNeedId;
    case ProductBoardColumnEntity.DesignInput:
      return findByDesignInputId;
    case ProductBoardColumnEntity.DesignOutput:
      return findByDesignOutputId;
    case ProductBoardColumnEntity.Verification:
      return findByVerificationId;
    default: {
      const { productValidationList, ...results } = find;
      return {
        ...results,
        productValidationPaginated: productValidationList,
        productValidationList: productValidationList?.data
      };
    }
  }
}

export function useFindProductValidationById(
  productId: number,
  validationId: number
) {
  const { data, isLoading, error, get } = useApiGet<ProductValidation>(
    `products/${productId}/validations/${validationId}`
  );
  return {
    productValidation: data,
    isLoadingProductValidation: isLoading,
    findProductValidationError: error,
    findProductValidation: get
  };
}

export function useAssignValidationUserNeed(
  productId: number,
  validationId: number
) {
  const { data, isLoading, error, post } = useApiPost<ProductValidation>(
    `products/${productId}/validations/${validationId}/user-needs`
  );
  return {
    updatedProductValidation: data,
    isAssigning: isLoading,
    error,
    assignUserNeeds: post
  };
}

export function useAssignValidationDesignInput(
  productId: number,
  validationId: number
) {
  const { data, isLoading, error, post } = useApiPost<ProductValidation>(
    `products/${productId}/validations/${validationId}/design-inputs`
  );
  return {
    updatedProductValidation: data,
    isAssigning: isLoading,
    error,
    assignDesignInputs: post
  };
}

export function useCreateProductDesignSpec(productId: number) {
  const { data, isLoading, error, post } = useApiPost<ProductDesignSpec>(
    `products/${productId}/design-specs`
  );
  return {
    createdProductDesignSpec: data,
    isCreatingProductDesignSpec: isLoading,
    createProductDesignSpecError: error,
    createProductDesignSpec: post
  };
}
export function useUpdateProductDesignSpec(
  productId: number,
  productDesignSpecId?: number
) {
  const [path, setPath] = useState('');
  const [designSpec, setDesignSpec] = useState<Partial<ProductDesignSpec>>();
  const { data, isLoading, error, patch } =
    useApiPatch<ProductDesignSpec>(path);
  const updateProductDesignSpec = useCallback(
    (designSpec: Partial<ProductDesignSpec>) => {
      setPath(
        `products/${productId}/design-specs/${
          productDesignSpecId || designSpec.id
        }`
      );
      setDesignSpec(designSpec);
    },
    [productId, productDesignSpecId]
  );
  useEffect(() => {
    if (path && designSpec) {
      patch(designSpec);
    }
  }, [patch, path, designSpec]);

  return {
    updatedProductDesignSpec: data,
    isUpdatingProductDesignSpec: isLoading,
    updateProductDesignSpecError: error,
    updateProductDesignSpec
  };
}
export function useFindProductDesignSpecs(productId: number) {
  const { data, isLoading, error, get } = useApiGet<
    PaginatedList<ProductDesignSpec>
  >(`products/${productId}/design-specs`);
  const findProductDesignSpecs = useIdSearchableFetch(
    get,
    WorkflowEntity.DesignSpec
  );
  return {
    productDesignSpecList: data,
    isLoadingProductDesignSpecs: isLoading,
    loadingProductDesignSpecsError: error,
    findProductDesignSpecs
  };
}

export function useFindProductDesignSpecsByUserNeedId(
  productId: number,
  userNeedId: number
) {
  const { data, isLoading, error, get } = useApiGet<ProductDesignSpec[]>(
    `products/${productId}/user-needs/${userNeedId}/design-specs`
  );
  const findProductDesignSpecs = useIdSearchableFetch(
    get,
    WorkflowEntity.DesignSpec
  );
  return {
    productDesignSpecList: data,
    isLoadingProductDesignSpecs: isLoading,
    loadingProductDesignSpecsError: error,
    findProductDesignSpecs
  };
}

export function useFindProductDesignSpecsByDesignInputId(
  productId: number,
  designInputId: number
) {
  const { data, isLoading, error, get } = useApiGet<ProductDesignSpec[]>(
    `products/${productId}/design-inputs/${designInputId}/design-specs`
  );
  const findProductDesignSpecs = useIdSearchableFetch(
    get,
    WorkflowEntity.DesignSpec
  );
  return {
    productDesignSpecList: data,
    isLoadingProductDesignSpecs: isLoading,
    loadingProductDesignSpecsError: error,
    findProductDesignSpecs
  };
}

export function useFindProductDesignSpecsByDesignOutputId(
  productId: number,
  designOutputId: number
) {
  const { data, isLoading, error, get } = useApiGet<ProductDesignSpec[]>(
    `products/${productId}/design-outputs/${designOutputId}/design-specs`
  );
  const findProductDesignSpecs = useIdSearchableFetch(
    get,
    WorkflowEntity.DesignSpec
  );
  return {
    productDesignSpecList: data,
    isLoadingProductDesignSpecs: isLoading,
    loadingProductDesignSpecsError: error,
    findProductDesignSpecs
  };
}

export function useFindProductDesignSpecsByVerificationId(
  productId: number,
  verificationId: number
) {
  const { data, isLoading, error, get } = useApiGet<ProductDesignSpec[]>(
    `products/${productId}/verifications/${verificationId}/design-specs`
  );
  const findProductDesignSpecs = useIdSearchableFetch(
    get,
    WorkflowEntity.DesignSpec
  );
  return {
    productDesignSpecList: data,
    isLoadingProductDesignSpecs: isLoading,
    loadingProductDesignSpecsError: error,
    findProductDesignSpecs
  };
}

export function useFindFilteredProductDesignSpecs(
  productId: number,
  productEntityId: number = 0,
  entity?: ProductBoardColumnEntity
): {
  productDesignSpecList: ProductDesignSpec[] | undefined;
  productDesignSpecPaginated?: PaginatedList<ProductDesignSpec>;
  isLoadingProductDesignSpecs: boolean;
  loadingProductDesignSpecsError: FetchError | undefined;
  findProductDesignSpecs: FetchApiCallback;
} {
  const find = useFindProductDesignSpecs(productId);
  const findByUserNeedId = useFindProductDesignSpecsByUserNeedId(
    productId,
    productEntityId
  );
  const findByDesignInputId = useFindProductDesignSpecsByDesignInputId(
    productId,
    productEntityId
  );
  const findByDesignOutputId = useFindProductDesignSpecsByDesignOutputId(
    productId,
    productEntityId
  );
  const findByVerificationId = useFindProductDesignSpecsByVerificationId(
    productId,
    productEntityId
  );

  switch (entity) {
    case ProductBoardColumnEntity.UserNeed:
      return findByUserNeedId;
    case ProductBoardColumnEntity.DesignInput:
      return findByDesignInputId;
    case ProductBoardColumnEntity.DesignOutput:
      return findByDesignOutputId;
    case ProductBoardColumnEntity.Verification:
      return findByVerificationId;
    default: {
      const { productDesignSpecList, ...results } = find;
      return {
        ...results,
        productDesignSpecPaginated: productDesignSpecList,
        productDesignSpecList: productDesignSpecList?.data
      };
    }
  }
}

export function useFindProductDesignSpecById(
  productId: number,
  designSpecId: number
) {
  const { data, isLoading, error, get } = useApiGet<ProductDesignSpec>(
    `products/${productId}/design-specs/${designSpecId}`
  );
  return {
    productDesignSpec: data,
    isLoadingProductDesignSpec: isLoading,
    findProductDesignSpecError: error,
    findProductDesignSpec: get
  };
}

export function useAssignDesignSpecUserNeed(
  productId: number,
  designSpecId: number
) {
  const { data, isLoading, error, post } = useApiPost<ProductDesignSpec>(
    `products/${productId}/design-specs/${designSpecId}/user-needs`
  );
  return {
    updatedProductDesignSpec: data,
    isAssigning: isLoading,
    error,
    assignUserNeeds: post
  };
}

export function useCreateProductHazard(productId: number) {
  const { data, isLoading, error, post } = useApiPost<Hazard>(
    `products/${productId}/hazards`
  );
  return {
    createdProductHazard: data,
    isCreatingProductHazard: isLoading,
    createProductHazardError: error,
    createProductHazard: post
  };
}

export function useGetPendingProductItems() {
  return useApiGet<PaginatedList<ProductEntity>>(`products/pending`);
}

export const getExportTitle = (entity: ProductExportEntity) => {
  switch (entity) {
    case ProductExportEntity.UserNeed:
      return getProductEntityName('userNeed');
    case ProductExportEntity.DesignInput:
      return getProductEntityName('designInput');
    case ProductExportEntity.DesignOutput:
      return getProductEntityName('designOutput');
    case ProductExportEntity.DesignSpec:
      return getProductEntityName('designSpec');
    case ProductExportEntity.Verification:
      return getProductEntityName('verification');
    case ProductExportEntity.Validation:
      return getProductEntityName('validation');
    case ProductExportEntity.TraceabilityReport:
      return 'Traceability Report';
    case ProductExportEntity.eClinicalReport:
      return 'eClinicalWorks Report';
    case ProductExportEntity.Hazard:
      return getProductEntityName('hazard');
  }
};

export function getProductExportFileName(
  templateName: string,
  product?: Product
): string {
  const productName: string = product
    ? ` - ${product.name} - PRODUCT-${product.id}`
    : '';
  return templateName + productName + '.docx';
}

export function useExportProductEntityDocument(
  productId: number,
  templateId: number
): [ApiResult<any>, (params?: ApiParams) => void, (fileName: string) => void] {
  const { get, ...result } = useApiGet<ProductValidation>(
    `products/${productId}/export/${templateId}`
  );
  const { response } = result;
  const { setBlob, downloadDocument } = useDownloadBlob(response);

  const getDocument = useCallback(
    (params?: ApiParams) => {
      // remove old document
      setBlob(undefined);
      get(params);
    },
    [get, setBlob]
  );

  return [result, getDocument, downloadDocument];
}

export function getProductEntityPrefix(
  entity: ProductBoardColumnEntity
): string {
  switch (entity) {
    case ProductBoardColumnEntity.UserNeed:
      return `${getProductEntityIdPrefix('userNeed')}-`;
    case ProductBoardColumnEntity.DesignInput:
      return `${getProductEntityIdPrefix('designInput')}-`;
    case ProductBoardColumnEntity.DesignOutput:
      return `${getProductEntityIdPrefix('designOutput')}-`;
    case ProductBoardColumnEntity.Verification:
      return `${getProductEntityIdPrefix('verification')}-`;
    case ProductBoardColumnEntity.Validation:
      return `${getProductEntityIdPrefix('validation')}-`;
    case ProductBoardColumnEntity.DesignSpec:
      return `${getProductEntityIdPrefix('designSpec')}-`;
    default:
      return '';
  }
}

export function useProductEntityPrefix(
  entity: ProductBoardColumnEntity
): string {
  return useMemo(() => getProductEntityPrefix(entity), [entity]);
}

export function useProductEntityPrefixId(
  productEntity: ProductEntity | Partial<ProductEntity> | undefined,
  entity: ProductBoardColumnEntity
): string {
  return useMemo(() => {
    if (!productEntity?.id) {
      return '';
    }
    const { id } = productEntity;
    return getProductEntityPrefix(entity) + id;
  }, [entity, productEntity]);
}

export function useCreateProductComponent(productId: number) {
  const { data, isLoading, error, post } = useApiPost<ProductComponent>(
    `products/${productId}/components`
  );
  return {
    createdComponent: data,
    creatingComponent: isLoading,
    createComponentError: error,
    createComponent: post
  };
}

export function useUpdateProductComponent(
  productId: number,
  componentId: number
) {
  const { data, isLoading, error, patch } = useApiPatch<ProductComponent>(
    `products/${productId}/components/${componentId}`
  );
  return {
    updatedComponent: data,
    updatingComponent: isLoading,
    updateComponentError: error,
    updateComponent: patch
  };
}

export function useDeleteProductComponent(
  productId: number,
  componentId: number
) {
  const { data, isLoading, error, del } = useApiDelete<ProductComponent>(
    `products/${productId}/components/${componentId}`
  );
  return {
    deletedComponent: data,
    deletingComponent: isLoading,
    deleteComponentError: error,
    deleteComponent: del
  };
}

export function useFindProductComponents(
  id: number,
  shouldFetchOnMount = false
) {
  const { data, isLoading, error, get } = useApiGet<ProductComponent[]>(
    `products/${id}/components`
  );
  useEffect(() => {
    if (shouldFetchOnMount) {
      get();
    }
  }, [get, shouldFetchOnMount]);
  return {
    productComponents: data,
    isLoading,
    loadingError: error,
    findProductComponents: get
  };
}

export function useCreateProductRelease(productId: number) {
  const { data, isLoading, error, post } = useApiPost<ProductRelease>(
    `products/${productId}/releases`
  );
  return {
    createdRelease: data,
    creatingRelease: isLoading,
    createReleaseError: error,
    createRelease: post
  };
}

export function useUpdateProductRelease(productId: number, releaseId: number) {
  const { data, isLoading, error, patch } = useApiPatch<ProductRelease>(
    `products/${productId}/releases/${releaseId}`
  );
  return {
    updatedRelease: data,
    updatingRelease: isLoading,
    updateReleaseError: error,
    updateRelease: patch
  };
}

export function useDeleteProductRelease(productId: number, releaseId: number) {
  const { data, isLoading, error, del } = useApiDelete<ProductRelease>(
    `products/${productId}/releases/${releaseId}`
  );
  return {
    deletedRelease: data,
    deletingRelease: isLoading,
    deleteReleaseError: error,
    deleteRelease: del
  };
}

export function useFindProductReleases(
  productId: number,
  shouldFetchOnMount = false
) {
  const { data, isLoading, error, get } = useApiGet<ProductRelease[]>(
    `products/${productId}/releases`
  );
  useEffect(() => {
    if (shouldFetchOnMount) {
      get();
    }
  }, [get, shouldFetchOnMount]);
  return {
    productReleases: data,
    isLoading,
    loadingError: error,
    findProductReleases: get
  };
}

export function useProductViewParams(
  includeAllQueryParams: boolean = false
): string {
  const location = useLocation();
  const view: string = useMemo(() => {
    return (
      ['detail', 'settings', 'board', 'swimlane', 'trace'].find(
        (str: string) => {
          return location.pathname.includes(str);
        }
      ) || 'detail'
    );
  }, [location]);

  return useMemo(() => {
    if (!view) {
      return '';
    }
    const params: URLSearchParams = new URLSearchParams(
      includeAllQueryParams ? location.search : undefined
    );
    params.set('from', view);
    return '?' + params.toString();
  }, [view, location, includeAllQueryParams]);
}

export function useProductFilterQueryParams() {
  const [status, setStatus] = useQueryParam(
    'status',
    withDefault<string[], string[]>(ArrayParam as any, [])
  );
  const [assigneeId, setAssigneeId] = useQueryParam(
    'assigneeId',
    withDefault<number[], number[]>(NumericArrayParam as any, [])
  );
  const [assignedForReviewOnly, setAssignedForReviewOnly] = useQueryParam(
    'assignedForReviewOnly',
    withDefault(BooleanParam, false)
  );
  const [unassignedOnly, setUnassignedOnly] = useQueryParam(
    'unassignedOnly',
    withDefault(BooleanParam, false)
  );
  const [components, setComponents] = useQueryParam(
    'components',
    withDefault<number[], number[]>(NumericArrayParam as any, [])
  );
  const [releases, setReleases] = useQueryParam(
    'releases',
    withDefault<number[], number[]>(NumericArrayParam as any, [])
  );

  const options: ProductFilterOptions = useMemo(() => {
    const options: ProductFilterOptions = {
      status: status || [],
      components: (components || []).map(
        (id: number) => ({ id } as ProductComponent)
      ),
      releases: (releases || []).map(
        (id: number) => ({ id } as ProductRelease)
      ),
      assignee: assigneeId || [],
      unassignedOnly: unassignedOnly || false,
      assignedForReviewOnly: assignedForReviewOnly || false
    };
    return options;
  }, [
    status,
    assigneeId,
    components,
    releases,
    unassignedOnly,
    assignedForReviewOnly
  ]);

  const convertOptionsToParams = useCallback(
    (options: ProductFilterOptions): ApiParams => {
      const {
        assignee,
        status,
        components,
        releases,
        assignedForReviewOnly,
        unassignedOnly
      } = options;
      const params: ApiParams = {
        status,
        assignedForReviewOnly,
        unassignedOnly,
        assigneeId: assignee,
        components: components.map(
          (component: ProductComponent) => component.id
        ),
        releases: releases.map((release: ProductRelease) => release.id)
      };
      status?.length === 0 && delete params.status;
      assignee?.length === 0 && delete params.assigneeId;
      components.length === 0 && delete params.components;
      releases.length === 0 && delete params.releases;
      typeof assignedForReviewOnly !== 'boolean' &&
        delete params.assignedForReviewOnly;
      typeof unassignedOnly !== 'boolean' && delete params.unassignedOnly;
      return params;
    },
    []
  );

  const getParams = useCallback((): ApiParams => {
    return convertOptionsToParams(options);
  }, [convertOptionsToParams, options]);

  return {
    options,
    setStatus,
    setAssigneeId,
    setComponents,
    setReleases,
    setAssignedForReviewOnly,
    setUnassignedOnly,
    getParams,
    convertOptionsToParams
  };
}

export function useFindProductRelationMap(productId: number) {
  const { data, isLoading, error, get } = useApiGet<ProductRelationMap[]>(
    `products/${productId}/relations`
  );
  return {
    productRelationMap: data,
    isLoadingProductRelationMap: isLoading,
    findProductRelationMapError: error,
    findProductRelationMap: get
  };
}

export function useFindProductUserNeedVersion(
  productId: number,
  productUserNeedId: number,
  version?: number
) {
  let path = `products/${productId}/user-needs/${productUserNeedId}/version/${version}`;
  const { data, isLoading, error, get } =
    useApiGet<ProductUserNeedVersion>(path);

  return {
    productUserNeedVersion: data,
    isLoadingProductUserNeedVersion: isLoading,
    findProductUserNeedVersionError: error,
    findProductUserNeedVersion: get
  };
}

export function useFindProductDesignInputVersion(
  productId: number,
  productDesignInputId: number,
  version?: number
) {
  let path = `products/${productId}/design-inputs/${productDesignInputId}/version/${version}`;
  const { data, isLoading, error, get } =
    useApiGet<ProductDesignInputVersion>(path);

  return {
    productDesignInputVersion: data,
    isLoadingProductDesignInputVersion: isLoading,
    findProductDesignInputVersionError: error,
    findProductDesignInputVersion: get
  };
}

export function useFindProductDesignOutputVersion(
  productId: number,
  productDesignOutputId: number,
  version?: number
) {
  let path = `products/${productId}/design-outputs/${productDesignOutputId}/version/${version}`;
  const { data, isLoading, error, get } =
    useApiGet<ProductDesignOutputVersion>(path);

  return {
    productDesignOutputVersion: data,
    isLoadingProductDesignOutputVersion: isLoading,
    findProductDesignOutputVersionError: error,
    findProductDesignOutputVersion: get
  };
}

export function useFindProductVerificationVersion(
  productId: number,
  productVerificationId: number,
  version?: number
) {
  let path = `products/${productId}/verifications/${productVerificationId}/version/${version}`;
  const { data, isLoading, error, get } =
    useApiGet<ProductVerificationVersion>(path);

  return {
    productVerificationVersion: data,
    isLoadingProductVerificationVersion: isLoading,
    findProductVerificationVersionError: error,
    findProductVerificationVersion: get
  };
}

export function useFindProductValidationVersion(
  productId: number,
  productValidationId: number,
  version?: number
) {
  let path = `products/${productId}/validations/${productValidationId}/version/${version}`;
  const { data, isLoading, error, get } =
    useApiGet<ProductValidationVersion>(path);

  return {
    productValidationVersion: data,
    isLoadingProductValidationVersion: isLoading,
    findProductValidationVersionError: error,
    findProductValidationVersion: get
  };
}

export function useFindProductDesignSpecVersion(
  productId: number,
  productDesignSpecId: number,
  version?: number
) {
  let path = `products/${productId}/design-specs/${productDesignSpecId}/version/${version}`;
  const { data, isLoading, error, get } =
    useApiGet<ProductDesignSpecVersion>(path);

  return {
    productDesignSpecVersion: data,
    isLoadingProductDesignSpecVersion: isLoading,
    findProductDesignSpecVersionError: error,
    findProductDesignSpecVersion: get
  };
}

function getBoardSearchQuery(entity: WorkflowEntity): string {
  switch (entity) {
    case WorkflowEntity.UserNeed:
      return 'userNeed';
    case WorkflowEntity.DesignInput:
      return 'designInput';
    case WorkflowEntity.DesignOutput:
      return 'designOutput';
    case WorkflowEntity.DesignVerification:
      return 'verification';
    case WorkflowEntity.DesignValidation:
      return 'validation';
    case WorkflowEntity.DesignSpec:
      return 'designSpec';
    default:
      return '';
  }
}

const productBoardOptions: IdSearchableFetchOptions[] =
  ProductWorkflowEntities.map((entity: WorkflowEntity) => {
    return {
      entity,
      removeFrom: false,
      to: getBoardSearchQuery(entity).concat('SearchId')
    } as IdSearchableFetchOptions;
  });

export function useGetProductBoard(productId: number) {
  const { data, isLoading, error, get } = useApiGet<ProductBoardData>(
    `products/${productId}/board`
  );
  const getProductBoard = useIdSearchableFetch(get, productBoardOptions);
  return {
    productBoard: data,
    isLoading: isLoading,
    loadingError: error,
    getProductBoard
  };
}

export function useGetProductBoardReferences() {
  const [state, setState] = useState<{
    path: string;
    productId: number;
    referencedProductIds: number[];
    apiParams: ApiParams;
  }>();
  const {
    data,
    isLoading,
    error,
    get: apiGet
  } = useApiGet<ProductBoardData[]>(state?.path || '');
  const get = useIdSearchableFetch(apiGet, productBoardOptions);
  useEffect(() => {
    if (state) {
      const { referencedProductIds, apiParams } = state;
      get({
        referencedProductIds,
        ...apiParams
      });
    }
  }, [get, state]);
  const getProductBoardReferences = useCallback(
    (
      productId: number,
      referencedProductIds: number[],
      apiParams: ApiParams = {}
    ) => {
      setState({
        path: `products/${productId}/board/references`,
        productId,
        referencedProductIds,
        apiParams
      });
    },
    []
  );
  return {
    productBoardReferences: data,
    isLoadingReferences: isLoading,
    loadingReferencesError: error,
    getProductBoardReferences
  };
}

export function useDownloadDefaultTemplate(): [
  ApiResult<any>,
  (params: ApiParams & { entity: ProductExportEntity }) => void,
  (fileName: string) => void
] {
  const { get, ...result } = useApiGet<any>('products/default-template');
  const { response } = result;
  const { setBlob, downloadDocument } = useDownloadBlob(response);

  const getDocument = useCallback(
    (params: ApiParams & { entity: ProductExportEntity }) => {
      // remove old document
      setBlob(undefined);
      get(params);
    },
    [get]
  );

  return [result, getDocument, downloadDocument];
}

// product templates

export function useFindProductTemplates(productId: number) {
  const { data, isLoading, error, get } = useApiGet<ProductTemplate[]>(
    `products/${productId}/templates`
  );
  const findProductTemplates = useCallback(() => {
    get({
      includeEClinical:
        !!process.env.REACT_APP_CLIENT_ENV?.includes('eclinicalworks')
    });
  }, [get]);
  return {
    productTemplates: data,
    isLoading,
    loadingError: error,
    findProductTemplates
  };
}

export function useCreateProductTemplate(productId: number) {
  const { data, isLoading, error, post } = useApiPost<ProductTemplate>(
    `products/${productId}/templates`
  );
  const createProductTemplate = useCallback(
    (dto: Partial<ProductTemplate>) => post(dto),
    [post]
  );
  return {
    createdProductTemplate: data,
    isCreating: isLoading,
    createError: error,
    createProductTemplate
  };
}

export function useUpdateProductTemplate(
  productId: number,
  templateId: number
) {
  const { data, isLoading, error, patch } = useApiPatch<ProductTemplate>(
    `products/${productId}/templates/${templateId}`
  );
  const updateProductTemplate = useCallback(
    (dto: Partial<ProductTemplate>) => patch(dto),
    [patch]
  );
  return {
    updatedProductTemplate: data,
    isUpdating: isLoading,
    updateError: error,
    updateProductTemplate
  };
}

export function useDeleteProductTemplate(
  productId: number,
  templateId: number
) {
  const { data, isLoading, error, del } = useApiDelete<ProductTemplate>(
    `products/${productId}/templates/${templateId}`
  );
  return {
    deletedProductTemplate: data,
    isDeleting: isLoading,
    deleteError: error,
    deleteProductTemplate: del
  };
}
